import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Helmet } from "react-helmet";
import "./PaymentSuccessPage.css";

const BASE_URL = "https://fooapp.mx";
const API_URL = process.env.REACT_APP_API_URL || "https://api.fooapp.mx";

interface PaymentInfo {
  amount: number;
  description: string;
  status: string;
  paid_at: string | null;
  partner_name: string | null;
  partner_logo: string | null;
}

const API_BASE = (process.env.REACT_APP_API_URL || "https://api.fooapp.mx").replace(/\/graphql\/?$/, "");

const PaymentSuccessPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get("session_id");
  const orderId = searchParams.get("order_id");
  const partnerId = searchParams.get("partner_id");
  const [payment, setPayment] = useState<PaymentInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    // QR payment: order_id + partner_id params — fetch order details
    if (orderId && partnerId) {
      fetch(`${API_BASE}/api/pay-order/${partnerId}/${orderId}`)
        .then((res) => {
          if (!res.ok) throw new Error("Not found");
          return res.json();
        })
        .then((data) => {
          setPayment({
            amount: data.total,
            description: `Pedido #${orderId}`,
            status: "paid",
            paid_at: new Date().toISOString(),
            partner_name: data.restaurant_name,
            partner_logo: data.restaurant_logo,
          });
          setLoading(false);
        })
        .catch(() => {
          // Fallback: show success without details
          setPayment({
            amount: 0,
            description: `Pedido #${orderId}`,
            status: "paid",
            paid_at: new Date().toISOString(),
            partner_name: null,
            partner_logo: null,
          });
          setLoading(false);
        });
      return;
    }

    // Payment link: session_id param — fetch details from API
    if (!sessionId) {
      setLoading(false);
      setError(true);
      return;
    }

    fetch(`${API_BASE}/api/payment-link/status?session_id=${sessionId}`)
      .then((res) => {
        if (!res.ok) throw new Error("Not found");
        return res.json();
      })
      .then((data) => {
        setPayment(data);
        setLoading(false);
      })
      .catch(() => {
        setError(true);
        setLoading(false);
      });
  }, [sessionId, orderId, partnerId]);

  const formatAmount = (amount: number) =>
    amount.toLocaleString("es-MX", {
      style: "currency",
      currency: "MXN",
    });

  const formatDate = (dateStr: string) =>
    new Date(dateStr).toLocaleDateString("es-MX", {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });

  return (
    <>
      <Helmet>
        <title>
          {payment?.partner_name
            ? `Pago a ${payment.partner_name} | Foo`
            : "Pago | Foo"}
        </title>
        <meta name="theme-color" content="#292929" />
      </Helmet>

      <div className="payment-success-container">
        <div className="payment-success-card">
          <div className="payment-success-header">
            <img src={`${BASE_URL}/img/logo_svg.svg`} alt="Foo" />
          </div>

          {loading && (
            <div className="payment-loading">
              <div className="spinner" />
              <p style={{ color: "#888" }}>Cargando informacion del pago...</p>
            </div>
          )}

          {!loading && error && (
            <div className="payment-success-body">
              <div className="payment-error-icon">
                <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2.5}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </div>
              <h2>No se encontro el pago</h2>
              <p className="subtitle">
                El enlace de pago no es valido o ha expirado.
              </p>
            </div>
          )}

          {!loading && !error && payment && (
            <>
              <div className="payment-success-body">
                {payment.status === "paid" ? (
                  <div className="payment-success-icon">
                    <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2.5}
                        d="M5 13l4 4L19 7"
                      />
                    </svg>
                  </div>
                ) : (
                  <div
                    className="payment-success-icon"
                    style={{ background: "#FFBB28" }}
                  >
                    <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2.5}
                        d="M12 8v4m0 4h.01"
                      />
                    </svg>
                  </div>
                )}

                {payment.partner_logo && (
                  <img
                    src={payment.partner_logo?.startsWith("http") ? payment.partner_logo : `${BASE_URL}${payment.partner_logo}`}
                    alt={payment.partner_name || ""}
                    className="partner-logo"
                  />
                )}

                <h2>
                  {payment.status === "paid"
                    ? "Pago realizado con exito"
                    : "Pago pendiente"}
                </h2>
                <p className="subtitle">
                  {payment.status === "paid"
                    ? `Tu pago a ${payment.partner_name || "el comercio"} ha sido procesado correctamente.`
                    : `Tu pago a ${payment.partner_name || "el comercio"} esta siendo procesado.`}
                </p>

                <div className="payment-details">
                  {payment.partner_name && (
                    <div className="detail-row">
                      <span className="detail-label">Comercio</span>
                      <span className="detail-value">
                        {payment.partner_name}
                      </span>
                    </div>
                  )}
                  <div className="detail-row">
                    <span className="detail-label">Concepto</span>
                    <span className="detail-value">{payment.description}</span>
                  </div>
                  <div className="detail-row">
                    <span className="detail-label">Monto</span>
                    <span className="detail-value amount">
                      {formatAmount(payment.amount)}
                    </span>
                  </div>
                  {payment.paid_at && (
                    <div className="detail-row">
                      <span className="detail-label">Fecha</span>
                      <span className="detail-value">
                        {formatDate(payment.paid_at)}
                      </span>
                    </div>
                  )}
                </div>
              </div>

              <div className="payment-success-footer">
                <p>
                  Este pago fue procesado de forma segura a traves de Stripe.
                  <br />
                  Si tienes alguna duda, contacta directamente a{" "}
                  {payment.partner_name || "el comercio"}.
                </p>
              </div>
            </>
          )}
        </div>
      </div>
    </>
  );
};

export default PaymentSuccessPage;
